import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import ConsentSection from './ConsentSection';
import { GlobalContext } from '@/context';
import {
  INTAKE_LOCAL_STORAGE_PREFIX,
  INTAKE_PAGES,
  REQUIRED_CONSENT_KEYS,
} from '@/constants/intakeForm';
import { InvalidIntakeLink, IntakeNotFound } from './ErrorScreens';
import LifestyleSection from './LifestyleSection';
import LoadingOverlay from '@/components/LoadingOverlay';
import MedicalHistorySection from './MedicalHistorySection';
import MentalHealthSection from './MentalHealthSection';
import NoAuthTopBar from '@/components/NoAuthTopBar';
import PCPSection from './PCPSection';
import PatientInfoSection from './PatientInfoSection';
import SaveIndicator from './SaveIndicator';
import SubmittedView from './SubmittedView';
import { buildSubmitChecklist } from './validators';
import classNames from 'classnames';
import { patientIntakeService } from '@/services';

// Draft-only keys we store inside responses while the patient is editing so
// the auto-save also restores their checkbox/initials/signature progress.
// Stripped before submit so the persisted responses stays clean.
const DRAFT_KEYS = [
  '_consent_checks',
  '_consent_initials_input',
  '_signed_name_draft',
  '_attested_draft',
];

// Defensive guard: responses must always be a plain object before we spread it.
// If we ever receive a string (e.g. from a server that returned JSONB as text or
// from a corrupted localStorage entry), spreading it would create bogus integer
// keys like { 0: '{', 1: '}' } from the characters of the string and corrupt
// the submit payload. Treat anything non-object as empty.
const isPlainResponses = (v) =>
  !!v && typeof v === 'object' && !Array.isArray(v);

// Drop any keys that look like accidental positional indices (e.g. "0", "1")
// or empty-string keys. Real intake fields are always named strings like
// `date_of_birth` or `conditions`. This also rescues a record whose responses
// got corrupted by an earlier version of the client that spread a string.
const sanitizeResponses = (v) => {
  if (!isPlainResponses(v)) return {};
  const out = {};
  Object.keys(v).forEach((key) => {
    if (!key) return;
    if (/^\d+$/.test(key)) return;
    out[key] = v[key];
  });
  return out;
};

const stripDraftKeys = (responses) => {
  const next = sanitizeResponses(responses);
  DRAFT_KEYS.forEach((k) => delete next[k]);
  return next;
};

const PatientIntakeView = () => {
  const { appointmentId } = useParams();
  const history = useHistory();
  const { currentUser } = useContext(GlobalContext);

  const storageKey = `${INTAKE_LOCAL_STORAGE_PREFIX}${appointmentId}`;

  const [bootState, setBootState] = useState('loading'); // loading | ready | submitted | forbidden | notfound
  // setter is used to record the server's updated_at for merge-on-reconnect;
  // the value itself is never read because merges happen at write time.
  // eslint-disable-next-line no-unused-vars
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);
  const [responses, setResponses] = useState({});
  const [saveState, setSaveState] = useState('idle');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [showMissing, setShowMissing] = useState(false);

  const saveTimer = useRef(null);
  const retryAttempt = useRef(0);
  const dirtyRef = useRef(false);

  // -- localStorage helpers -------------------------------------------------

  const readLocal = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.updatedAt) return null;
      // If the responses field is anything other than a plain object (string,
      // number, array, null), the localStorage entry is corrupted. Drop it.
      if (!isPlainResponses(parsed.responses)) {
        try {
          localStorage.removeItem(storageKey);
        } catch (_) {
          // localStorage unavailable; nothing we can do here.
        }
        return null;
      }
      // Strip any numeric-string keys that crept in via a prior spread-string
      // bug so the recovered state is clean.
      parsed.responses = sanitizeResponses(parsed.responses);
      return parsed;
    } catch (e) {
      return null;
    }
  };

  const writeLocal = (nextResponses, updatedAt) => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          responses: nextResponses,
          updatedAt: updatedAt || new Date().toISOString(),
        }),
      );
    } catch (e) {
      // localStorage may be full or disabled in private mode. Non-fatal.
    }
  };

  const clearLocal = () => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      // Non-fatal.
    }
  };

  // -- Initial load ---------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: serverIntake } = await patientIntakeService.getIntake(
          appointmentId,
        );
        if (cancelled) return;

        // Completed: clear any stale local mirror, show success.
        if (serverIntake && serverIntake.status === 'completed') {
          clearLocal();
          setBootState('submitted');
          return;
        }

        const rawServerResponses = serverIntake && serverIntake.responses;
        // The backend column is JSONB but some response paths may serialize it
        // back as a string. Always coerce to a plain object so we never spread
        // characters into the responses state.
        let serverResponses = isPlainResponses(rawServerResponses)
          ? rawServerResponses
          : {};
        if (typeof rawServerResponses === 'string') {
          try {
            const parsed = JSON.parse(rawServerResponses);
            if (isPlainResponses(parsed)) serverResponses = parsed;
          } catch (_) {
            serverResponses = {};
          }
        }
        // Strip any bogus positional/empty keys an earlier client may have
        // written before this guard existed.
        serverResponses = sanitizeResponses(serverResponses);
        const serverUpdated =
          (serverIntake && serverIntake.updated_at) ||
          new Date(0).toISOString();
        setServerUpdatedAt(serverUpdated);

        const local = readLocal();
        let merged = serverResponses;
        if (local) {
          if (local.updatedAt > serverUpdated) {
            // Local edits are newer than what the server has. Take local and
            // immediately trigger a sync PATCH after we mount.
            merged = local.responses;
            dirtyRef.current = true;
          } else {
            // Server is newer (cross-device case). Overwrite local with server.
            merged = serverResponses;
            writeLocal(serverResponses, serverUpdated);
          }
        } else {
          // Seed local from server so the first edit has a baseline.
          writeLocal(serverResponses, serverUpdated);
        }
        setResponses(merged);
        setBootState('ready');
      } catch (err) {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        // Do NOT touch localStorage here — the responses might still be valid
        // for this user after they re-auth or open the correct link.
        //
        // 401 from this endpoint means the user IS authenticated but does not
        // own this intake. The backend returns 401 with an `errorMessage` body,
        // which makes the global axios interceptor re-throw instead of calling
        // logout, so we must terminate the loading state ourselves. Treat 401
        // and 403 identically: surface the InvalidIntakeLink screen with a
        // Sign Out button so the user can re-auth as the right account.
        if (status === 401 || status === 403) setBootState('forbidden');
        else if (status === 404) setBootState('notfound');
        else {
          // Generic — surface as not-found rather than a blank screen.
          setBootState('notfound');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  // -- Auto-save (debounced 1s + backoff retry) -----------------------------

  const pushSave = async (snapshot) => {
    setSaveState('saving');
    try {
      const { data: serverIntake } = await patientIntakeService.updateIntake(
        appointmentId,
        snapshot,
      );
      if (serverIntake && serverIntake.updated_at) {
        setServerUpdatedAt(serverIntake.updated_at);
        writeLocal(snapshot, serverIntake.updated_at);
      } else {
        writeLocal(snapshot);
      }
      retryAttempt.current = 0;
      setSaveState('saved');
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 401 || status === 403) {
        setBootState('forbidden');
        return;
      }
      if (status === 404) {
        setBootState('notfound');
        return;
      }
      // Transient errors: backoff 2s, 8s, 32s, then give up.
      const delays = [2000, 8000, 32000];
      if (retryAttempt.current < delays.length) {
        const delay = delays[retryAttempt.current];
        retryAttempt.current += 1;
        setSaveState('saving');
        setTimeout(() => pushSave(snapshot), delay);
      } else {
        setSaveState('failed');
      }
    }
  };

  useEffect(() => {
    if (bootState !== 'ready') return undefined;
    if (!dirtyRef.current) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState('saving');
    const snapshot = responses;
    saveTimer.current = setTimeout(() => {
      pushSave(snapshot);
    }, 1000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [responses, bootState]);

  // Mark dirty + mirror to localStorage on every edit so a tab crash within
  // the 1s debounce window doesn't lose work.
  const setResponsesAndDirty = (updater) => {
    setResponses((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      writeLocal(next);
      return next;
    });
    dirtyRef.current = true;
  };

  const setField = (key, value) => {
    setResponsesAndDirty((prev) => ({
      ...sanitizeResponses(prev),
      [key]: value,
    }));
  };

  // -- Consent / signature draft slots --------------------------------------
  const initials = (responses && responses._consent_initials_input) || '';
  const setInitials = (v) => setField('_consent_initials_input', v);

  const consents = useMemo(
    () => (responses && responses._consent_checks) || {},
    [responses],
  );
  const setConsents = (next) => setField('_consent_checks', next);

  const signedName = (responses && responses._signed_name_draft) || '';
  const setSignedName = (v) => setField('_signed_name_draft', v);

  const attested = !!(responses && responses._attested_draft);
  const setAttested = (v) => setField('_attested_draft', v);

  const defaultName =
    currentUser && (currentUser.first_name || currentUser.last_name)
      ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
      : '';

  // -- Submit ---------------------------------------------------------------
  const submitChecklist = useMemo(
    () =>
      buildSubmitChecklist({
        responses,
        initials,
        consents,
        signedName,
        attested,
      }),
    [responses, initials, consents, signedName, attested],
  );
  const canSubmit = submitChecklist.length === 0;

  const focusFirstMissing = () => {
    setShowMissing(true);
    const first = submitChecklist[0];
    if (!first) return;
    if (first.pageIndex !== pageIndex) {
      setPageIndex(first.pageIndex);
    }
    setTimeout(() => {
      const el = document.getElementById(first.fieldId);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function') {
          el.focus({ preventScroll: true });
        }
      }
    }, 60);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      focusFirstMissing();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    // Flush any pending debounced save before submit so we don't lose the
    // very last keystroke.
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }

    const cleanedResponses = stripDraftKeys(responses);
    const consent_initials = REQUIRED_CONSENT_KEYS.reduce((acc, key) => {
      acc[key] = initials;
      return acc;
    }, {});

    // Encode the typed signature into a non-empty data URI so the backend
    // joi-required `signature_data` field passes without backend changes.
    let signature_data;
    try {
      signature_data = `data:text/plain;base64,${btoa(
        unescape(encodeURIComponent(signedName)),
      )}`;
    } catch (e) {
      signature_data = `data:text/plain;base64,${btoa(signedName)}`;
    }

    try {
      await patientIntakeService.submitIntake(appointmentId, {
        responses: cleanedResponses,
        signed_name: signedName.trim(),
        signature_data,
        consent_initials,
      });
      clearLocal();
      setBootState('submitted');
    } catch (err) {
      const status = err && err.response && err.response.status;
      const message =
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.errorMessage) ||
        (err && err.message) ||
        'We could not submit your form. Please try again.';
      if (status === 401 || status === 403) {
        setBootState('forbidden');
      } else if (status === 404) {
        setBootState('notfound');
      } else {
        setSubmitError(message);
      }
    }
    setSubmitting(false);
  };

  const handleSaveAndExit = () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    // Fire-and-forget final save, then leave. localStorage is already mirrored.
    pushSave(responses);
    history.push('/');
  };

  // -- Render guards --------------------------------------------------------

  if (bootState === 'loading') {
    return (
      <div className="w-screen h-screen bg-mmj-gray flex items-center justify-center">
        <LoadingOverlay show />
      </div>
    );
  }
  if (bootState === 'submitted') return <SubmittedView />;
  if (bootState === 'forbidden') return <InvalidIntakeLink />;
  if (bootState === 'notfound') return <IntakeNotFound />;

  // -- Page rendering -------------------------------------------------------

  const currentPage = INTAKE_PAGES[pageIndex];

  const renderPage = () => {
    switch (currentPage.id) {
      case 'patient_info':
        return (
          <PatientInfoSection
            responses={responses}
            setField={setField}
            errors={showMissing ? {} : {}}
          />
        );
      case 'medical_history':
        return (
          <MedicalHistorySection responses={responses} setField={setField} />
        );
      case 'lifestyle':
        return <LifestyleSection responses={responses} setField={setField} />;
      case 'mental_health':
        return (
          <MentalHealthSection responses={responses} setField={setField} />
        );
      case 'pcp':
        return <PCPSection responses={responses} setField={setField} />;
      case 'consent':
        return (
          <ConsentSection
            responses={responses}
            setField={setField}
            initials={initials}
            setInitials={setInitials}
            consents={consents}
            setConsents={setConsents}
            signedName={signedName}
            setSignedName={setSignedName}
            attested={attested}
            setAttested={setAttested}
            defaultName={defaultName}
          />
        );
      default:
        return null;
    }
  };

  const isLastPage = pageIndex === INTAKE_PAGES.length - 1;
  const progressPercent = Math.round(
    ((pageIndex + 1) / INTAKE_PAGES.length) * 100,
  );

  return (
    <div className="flex flex-col w-screen min-h-screen bg-mmj-gray">
      <NoAuthTopBar />

      <div className="w-full max-w-2xl mx-auto px-4 py-6 md:py-10 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="text-base font-semibold text-mmj-header">
            Page {pageIndex + 1} of {INTAKE_PAGES.length}
          </div>
          <button
            type="button"
            onClick={handleSaveAndExit}
            className="text-base font-semibold text-mmj-blue hover:underline"
            style={{ minHeight: '44px' }}
          >
            Save and finish later
          </button>
        </div>

        <div
          className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-6"
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Page ${pageIndex + 1} of ${INTAKE_PAGES.length}`}
        >
          <div
            className="h-full bg-mmj-blue"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-mmj-header mb-6">
          {currentPage.label}
        </h1>

        <div className="bg-white rounded-xl shadow-mmj-lg p-4 md:p-8 mb-6">
          {renderPage()}
        </div>

        {isLastPage && showMissing && submitChecklist.length > 0 && (
          <div
            className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 md:p-5 mb-6"
            role="alert"
          >
            <div className="text-lg font-bold text-yellow-900 mb-2">
              Almost done — {submitChecklist.length} item
              {submitChecklist.length === 1 ? '' : 's'} still needed
            </div>
            <ul className="space-y-2">
              {submitChecklist.map((item, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => {
                      setPageIndex(item.pageIndex);
                      setTimeout(() => {
                        const el = document.getElementById(item.fieldId);
                        if (el) {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                          if (typeof el.focus === 'function') {
                            el.focus({ preventScroll: true });
                          }
                        }
                      }, 60);
                    }}
                    className="text-base text-yellow-900 underline text-left"
                    style={{ minHeight: '44px' }}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {submitError && (
          <div className="p-4 bg-red-50 text-red-900 font-semibold rounded-xl mb-6">
            {submitError}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              setShowMissing(false);
              setPageIndex(Math.max(0, pageIndex - 1));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            disabled={pageIndex === 0 || submitting}
            className={classNames(
              'rounded-full font-bold border-2 px-6 transition-colors',
              'bg-white text-mmj-header border-gray-300',
            )}
            style={{ minHeight: '64px', fontSize: '20px', minWidth: '120px' }}
          >
            Back
          </button>

          {!isLastPage && (
            <button
              type="button"
              onClick={() => {
                setPageIndex(Math.min(INTAKE_PAGES.length - 1, pageIndex + 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={submitting}
              className="flex-1 rounded-full font-bold bg-mmj-blue text-white"
              style={{ minHeight: '64px', fontSize: '20px' }}
            >
              Continue
            </button>
          )}

          {isLastPage && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className={classNames(
                'flex-1 rounded-full font-bold',
                canSubmit
                  ? 'bg-mmj-blue text-white'
                  : 'bg-mmj-blue text-white opacity-90',
              )}
              style={{ minHeight: '64px', fontSize: '20px' }}
            >
              {submitting ? 'Submitting…' : 'Submit my intake form'}
            </button>
          )}
        </div>
      </div>

      <SaveIndicator
        state={saveState}
        onRetry={() => {
          retryAttempt.current = 0;
          pushSave(responses);
        }}
      />
    </div>
  );
};

export default PatientIntakeView;
